/**
 * Tenants + identity API client.
 *
 * The X-Compass-Tenant header is set by axios interceptor (see client.ts);
 * we read the user's current tenant from the auth store and inject it.
 */
import { apiGet } from './client'

export interface MeResponse {
  user: {
    id: string
    username: string
    display_name: string | null
    is_superuser: boolean
  }
  current_tenant: {
    id: string
    slug: string
    name: string
  }
  permissions: string[]
  inheritance_path: string[]
}

export interface TenantSummary {
  id: string
  slug: string
  name: string
  description: string | null
  parent_tenant_id: string | null
  is_active: boolean
  audit_retention_days: number
}

export interface TenantsListResponse {
  count: number
  tenants: TenantSummary[]
}

export interface TenantMember {
  user_id: string
  username: string
  display_name: string | null
  is_superuser: boolean
  role: string
  granted_at: string
}

export interface TenantBrokerAccount {
  id: string
  alias: string
  ib_account_id: string
  account_name: string
  is_active: boolean
}

export interface TenantDetailResponse {
  id: string
  slug: string
  name: string
  description: string | null
  parent_tenant_id: string | null
  audit_retention_days: number
  members: TenantMember[]
  broker_accounts: TenantBrokerAccount[]
}

export interface AuditEvent {
  id: string
  user_id: string | null
  username: string | null
  action_type: string
  resource_type: string | null
  resource_id: string | null
  old_value: unknown
  new_value: unknown
  via_superuser: boolean
  request_ip: string | null
  risk_level: number
  created_at: string
}

export interface TenantAuditResponse {
  count: number
  events: AuditEvent[]
}

export const getMe = () => apiGet<MeResponse>('/auth/me')
export const getTenants = () => apiGet<TenantsListResponse>('/tenants')
export const getTenantDetail = (id: string) =>
  apiGet<TenantDetailResponse>(`/tenants/${id}`)
export const getTenantAudit = (id: string, limit = 100) =>
  apiGet<TenantAuditResponse>(`/tenants/${id}/audit`, { params: { limit } })
